import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import CommonTypeAhead from '../../formsAndTable/forms/formWidgets/typeAhead/common/CommonTypeAhead';
import {
  AssistantExamDataCreate,
  CourseCategory,
  CourseData,
  CourseLesson,
  ExamData,
  StudentCourseLessonExam,
  UserData,
} from '../../../types/Tables';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { getStudentCourseLessonExams, getStudentCourses, getUsers } from '../../../services/api';
import { OptionItems } from '../../../types/Forms';
import { Option } from 'react-bootstrap-typeahead/types/types';

import { Primary, Secondary, SweetAlert } from '../../../constants';
import { hideSwalLoader, showSwalLoader } from '../../bonusUi/sweetAlert/Loader';
import { getCalendarStops, unlockBodyScroll } from '../../../lib/utils';
import { useTranslation } from 'react-i18next';
import CommonCardHeader from '../../../coreComponents/CommonCardHeader';
type CreateExamFormProps = {
  defaults: Partial<AssistantExamDataCreate>;
  useDefaults?: boolean;
  close: () => void;
  createAssistantExam: (data: {
    userId: number;
    courseId: number;
    lessonId: number;
    date: Date;
    prompt: string;
    googleMeetLink: string;
    studentSetExamId: number;
    parentExamId?: number | null;
    examLang: string;
  }) => any;
};

const CreateExamForm = (props: CreateExamFormProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedExam, setSelectedExam] = useState<StudentCourseLessonExam | null>(null);
  const [usersNames, setUsersNames] = useState(null);
  const [selectedUser, setSelectedUser] = useState<OptionItems | null>(null);
  const [userCourses, setUserCourses] = useState<CourseData[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Partial<CourseData> | null>(null);
  const [userLessons, setUserLessons] = useState<CourseLesson[]>([]);
  const [userExams, setUserExams] = useState<StudentCourseLessonExam[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Partial<CourseLesson> | null>(null);
  const [userAppointedExamId, setUserAppointedExamId] = useState<number>(0);
  const [meetLink, setMeetLink] = useState('');
  const [linkError, setLinkError] = useState(false);
  const [initing, setIniting] = useState(true);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const hardStop = async (title: string, text: string) => {
    await Swal.fire({ icon: 'warning', title, text, confirmButtonText: 'OK' }).then(() =>
      props.close(),
    );

    setTimeout(unlockBodyScroll);
  };
  const { t, i18n } = useTranslation();
  const locale = i18n?.language || navigator.language;

  const dtf = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        hour12: false,
        // timeZone: 'Europe/Kyiv', // <- uncomment if you want Kyiv time for everyone
      }),
    [locale],
  );

  useEffect(() => {
    if (!props.useDefaults) {
      setIniting(false);
      return;
    }

    const init = async () => {
      try {
        const { id, name, courseId, lessonId, date } = props.defaults || {};
        if (!id || !name || !courseId || !lessonId || !date) {
          return hardStop('Invalid data', 'Missing user/course/lesson/date.');
        }

        setIniting(true);

        // 1) user
        const user = { id, name };
        if (mountedRef.current) setSelectedUser(user);

        // 2) courses for user
        const courses = await getStudentCourses(id);
        if (!courses?.length) {
          return hardStop('No courses', 'Selected user has no courses.');
        }
        if (mountedRef.current) setUserCourses(courses);

        // 3) course
        const course =
          courses.find((c: CourseData) => c.id === courseId) ?? ({ id: courseId } as CourseData);
        if (!course) {
          return hardStop('Course not found', 'Default course does not exist for this user.');
        }
        if (mountedRef.current) setSelectedCourse(course);
        // 4) lessons (from course)
        const lessons = course.categories?.flatMap((cat: CourseCategory) => cat.lessons) ?? [];
        if (!lessons.length) {
          return hardStop('No lessons', 'This course has no lessons to choose from.');
        }
        if (mountedRef.current) setUserLessons(lessons);

        // 5) lesson
        const lesson =
          lessons.find((l: CourseLesson) => l.id === lessonId) ??
          ({ id: lessonId } as CourseLesson);
        setSelectedLesson(lesson);
        if (!lesson) {
          return hardStop('Lesson not found', 'Default lesson is not available in this course.');
        }
        if (mountedRef.current) setSelectedLesson(lesson);
        // 6) exams for this (user, course, lesson)
        const exams = await getStudentCourseLessonExams({ studentId: id, courseId, lessonId });
        if (mountedRef.current) setUserExams(exams);

        // 7) pick exam by exact date (or fallback to first)
        const desiredTs = new Date(date).getTime();
        const match =
          exams.find((e: StudentCourseLessonExam) => new Date(e.date).getTime() === desiredTs) ??
          exams[0] ??
          null;
        if (match && mountedRef.current) {
          setSelectedExam(match);
          setSelectedDate(new Date(match.date));
          setUserAppointedExamId(match.id);
        } else if (mountedRef.current) {
          setSelectedExam(null);
          setSelectedDate(null);
          setUserAppointedExamId(0);
        }
      } catch (error) {
        return hardStop('Error', 'Failed to initialize the form.');
      } finally {
        setIniting(false);
      }
    };

    void init();
  }, [props.useDefaults, props.defaults]);

  useEffect(() => {
    const getAllUsers = async () => {
      const res = await getUsers();

      const names = res.map((el: UserData) => {
        return {
          id: el.id,
          name: `${el.firstName} ${el.lastName}`,
        };
      });
      setUsersNames(names);
    };

    getAllUsers();
    // getCalendarStops(setCalendarStopPeriods);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    checkGoogleMeetLink(meetLink);
    if (
      !selectedDate ||
      !selectedUser ||
      !selectedCourse ||
      !selectedLesson ||
      !selectedCourse?.id ||
      !selectedUser?.id ||
      !selectedLesson.id ||
      !selectedExam
    ) {
      return;
    }

    if (linkError) {
      Swal.fire({
        icon: 'error',
        text: t('bot.invalidMeetLink'),
      });
      return;
    }

    const body = {
      userId: selectedUser.id,
      courseId: selectedCourse.id,
      lessonId: selectedLesson.id,
      date: selectedDate,
      prompt:
        selectedExam.examLang === 'ru'
          ? (props.defaults?.prompt?.ru ?? '')
          : (props.defaults?.prompt?.uk ?? ''),
      googleMeetLink: meetLink,
      studentSetExamId: userAppointedExamId,
      parentExamId: selectedExam?.parentExamId,
      examLang: selectedExam.examLang || 'ru',
    };
    try {
      showSwalLoader(t('exams.generatingExam'));
      const createdExam = await props.createAssistantExam(body);
      hideSwalLoader();
      if (createdExam.error) {
        Swal.fire({
          text: t('exams.generatingExamError'),
          icon: 'error',
          confirmButtonColor: Secondary,
        });
      } else if (createdExam.success) {
        Swal.fire({
          text: t('exams.generatingExamSuccess'),
          icon: 'success',
          confirmButtonColor: Primary,
        }).then((result) => {
          if (result.isConfirmed) {
            props.close();
          }
        });
      }
    } catch (error) {
      hideSwalLoader();
      Swal.fire({
        text: t('exams.generatingExamError'),
        icon: 'error',
        confirmButtonColor: Secondary,
      });
      console.log('error', error);
    }
  };

  const handleUserSelect = async (selected: Option[]) => {
    if (selected.length > 0 && typeof selected[0] === 'object' && 'id' in selected[0]) {
      const user = selected[0] as OptionItems;
      setSelectedUser(user);
      const courses = await getStudentCourses(user.id!);
      setUserCourses(courses);
    } else {
      setSelectedUser(null);
    }
  };
  const handleCourseSelect = async (selected: Option[]) => {
    if (selected.length > 0 && typeof selected[0] === 'object') {
      const course = selected[0] as CourseData;
      setSelectedCourse(course);
      createLessonsList(course);
    }
  };

  const createLessonsList = (course: CourseData) => {
    const allLessons = course.categories.flatMap((category) => category.lessons);
    setUserLessons(allLessons);
  };

  const createExamDatesList = async (lessonId: number) => {
    try {
      const req = {
        studentId: selectedUser?.id,
        courseId: selectedCourse?.id,
        lessonId: lessonId,
      };
      const exams = await getStudentCourseLessonExams(req);
      setUserExams(exams);
      if (exams.length === 0) {
        Swal.fire({
          title: 'No exams',
          text: `User has no appoined exams for these course and lesson`,
          icon: 'warning',
          confirmButtonText: 'Ok',
          showConfirmButton: true,
        }).then(() => setSelectedLesson(null));
      }
    } catch (error) {
      console.log('createExamDatesList error', error);
    }
  };

  const handleLessonSelect = (selected: Option[]) => {
    if (selected.length > 0 && typeof selected[0] === 'object') {
      const lesson = selected[0] as CourseLesson;
      setSelectedLesson(lesson);
      createExamDatesList(lesson.id);
    }
  };

  const handleDateSelect = (selected: Option[]) => {
    if (selected.length > 0 && typeof selected[0] === 'object') {
      const exam = selected[0] as StudentCourseLessonExam;
      setSelectedExam(exam);
      setSelectedDate(new Date(exam.date));
      setUserAppointedExamId(exam.id);
    } else {
      setSelectedExam(null);
      setSelectedDate(null);
      setUserAppointedExamId(0);
    }
  };

  const checkGoogleMeetLink = (link: string) => {
    const pureLink = link.trim().split('?')[0];
    // normalize: remove trailing slashes
    const normalized = pureLink.replace(/\/+$/, '');

    // Extract potential code part (after domain) or use the whole input
    let codePart = normalized;
    const hostToken = 'meet.google.com/';
    const lower = normalized.toLowerCase();
    if (lower.includes(hostToken)) {
      codePart = normalized.slice(lower.indexOf(hostToken) + hostToken.length);
    } else {
      // remove protocol if present
      codePart = normalized.replace(/^https?:\/\//i, '');
    }

    const plainRe = /^[A-Za-z0-9]{10}$/;
    const groupedRe = /^[A-Za-z0-9]{3}-[A-Za-z0-9]{4}-[A-Za-z0-9]{3}$/;

    const ok = plainRe.test(codePart) || groupedRe.test(codePart);
    setLinkError(!ok);

    if (ok) {
      // store a normalized full meet URL when possible
      if (normalized.startsWith('http')) {
        setMeetLink(normalized);
      } else {
        setMeetLink(`https://meet.google.com/${codePart}`);
      }
    }
  };

  return (
    <Col xl="12">
      {!initing && (
        <Card>
          <CardBody>
            <Form className="needs-validation custom-input" onSubmit={(e) => handleSubmit(e)}>
              <Row>
                <Col sm="6">
                  {usersNames && (
                    <CommonTypeAhead
                      id="blood"
                      title={t('assistantExam.createForm.user')}
                      // subTitle={BloodHoundSubTitle}
                      options={usersNames}
                      placeholder={t('assistantExam.createForm.user')}
                      labelKey={'name'}
                      onChange={(selected) => handleUserSelect(selected)}
                      disabled={selectedUser !== null}
                      selected={selectedUser ? [selectedUser] : []}
                      className={`${selectedUser !== null && 'bg-[#f1f1f1]'}`}
                    />
                  )}
                </Col>

                <Col sm="6">
                  <CommonTypeAhead
                    id="courses"
                    title={t('assistantExam.createForm.course')}
                    options={userCourses} // ✅ Array<CourseData>
                    placeholder={t('assistantExam.createForm.course')}
                    labelKey={'name'}
                    disabled={!userCourses.length || selectedCourse !== null}
                    onChange={handleCourseSelect}
                    selected={selectedCourse ? [selectedCourse] : []}
                  />
                </Col>
              </Row>

              <Row>
                <Col sm="6">
                  {' '}
                  <CommonTypeAhead
                    id="lessons"
                    title={t('assistantExam.createForm.lesson')}
                    options={userLessons} // ✅ Array<CourseData>
                    placeholder={t('assistantExam.createForm.lesson')}
                    labelKey={'title'}
                    // labelKey={() => {
                    //   return 'title';
                    // }}
                    disabled={!userLessons.length || selectedLesson !== null}
                    onChange={handleLessonSelect}
                    selected={selectedLesson ? [selectedLesson] : []}
                  />
                </Col>
                <Col sm="6">
                  {' '}
                  <CommonTypeAhead
                    id="exam_dates"
                    title={t('assistantExam.createForm.examDate')}
                    options={userExams} // ✅ Array<CourseData>
                    placeholder={t('assistantExam.createForm.examDate')}
                    labelKey={(o: StudentCourseLessonExam) => dtf.format(new Date(o.date))}
                    disabled={!userExams.length || selectedExam !== null}
                    onChange={handleDateSelect}
                    selected={selectedExam ? [selectedExam] : []}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm="6">
                  <Col sm="12">
                    <Card>
                      <CommonCardHeader title={t('bot.formSubTitle')} />
                      <CardBody>
                        <div>
                          <Form className="theme-form">
                            <div className={'position-relative'}>
                              <Input
                                name="meet-link"
                                id="meet-link"
                                type="text"
                                className={`${linkError && 'is-invalid'}`}
                                onChange={(e) => {
                                  setLinkError(false);
                                  setMeetLink(e.target.value);
                                }}
                                value={meetLink}
                                onBlur={(e) => checkGoogleMeetLink(e.target.value)}
                                maxLength={100}
                                required
                              />
                              {linkError && (
                                <div className="m-0 invalid-feedback text-danger position-absolute -bottom-[0.5rem]">
                                  {t('bot.invalidMeetLink')}
                                </div>
                              )}
                            </div>
                          </Form>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Col>
              </Row>
              <Row>
                <Col sm="12" className="mt-3">
                  <h4 className="mb-2">{t('assistantExam.createForm.prompt')}</h4>
                  <p>
                    {selectedExam?.examLang === 'ru'
                      ? props.defaults?.prompt?.ru
                      : props.defaults?.prompt?.uk}
                  </p>
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col sm="auto" className="d-flex gap-3 mt-3">
                  <Button
                    type="submit"
                    color="primary"
                    outline
                    disabled={meetLink === '' || linkError}
                  >
                    {t('global.ok')}
                  </Button>
                  <Button type="button" outline onClick={props.close}>
                    {t('global.cancel')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      )}
    </Col>
  );
};
export default CreateExamForm;
